import React, { useMemo } from 'react';
import { Incident } from '../types/incident';
import { initialCaseTypes } from '../data/caseTypes';
import { getStateName } from '../data/geography';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { AlertTriangle, Clock, Activity, ShieldAlert, ArrowRight } from 'lucide-react';
interface DashboardProps {
  incidents: Incident[];
  onNavigate: (menu: string) => void;
}
const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e']; // Critical, High, Medium, Low

export function Dashboard({
  incidents,
  onNavigate
}: DashboardProps) {
  const approvedIncidents = useMemo(() => incidents.filter(i => i.status === 'Approved'), [incidents]);
  const pendingIncidents = useMemo(() => incidents.filter(i => i.status === 'Submitted'), [incidents]);

  // Metrics
  const totalApproved = approvedIncidents.length;
  const totalPending = pendingIncidents.length;
  const totalDeaths = approvedIncidents.reduce((sum, inc) => sum + inc.numDeaths, 0);
  const totalInjuries = approvedIncidents.reduce((sum, inc) => sum + inc.numInjuries, 0);
  const criticalCount = approvedIncidents.filter(i => i.severity === 'Critical').length;

  // Severity Data for Pie Chart
  const severityData = useMemo(() => {
    const counts = {
      Critical: 0,
      High: 0,
      Medium: 0,
      Low: 0
    };
    approvedIncidents.forEach(inc => {
      if (counts[inc.severity as keyof typeof counts] !== undefined) {
        counts[inc.severity as keyof typeof counts]++;
      }
    });
    return [{
      name: 'Critical',
      value: counts.Critical
    }, {
      name: 'High',
      value: counts.High
    }, {
      name: 'Medium',
      value: counts.Medium
    }, {
      name: 'Low',
      value: counts.Low
    }].filter(d => d.value > 0);
  }, [approvedIncidents]);

  // Monthly Trend Data
  const trendData = useMemo(() => {
    const counts: Record<string, number> = {};
    approvedIncidents.forEach(inc => {
      const month = inc.date.substring(0, 7);
      counts[month] = (counts[month] || 0) + 1;
    });
    return Object.entries(counts).map(([month, count]) => ({
      month,
      count
    })).sort((a, b) => a.month.localeCompare(b.month)).slice(-6); // Last 6 months
  }, [approvedIncidents]);

  // Recent Incidents
  const recentIncidents = useMemo(() => {
    return [...incidents].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  }, [incidents]);
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-224" data-mp="39tt0l9gftb73">
      <div data-id="element-225" data-mp="39uchcmucptjj">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-226" data-mp="39rghgsyy0usg">System Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1" data-id="element-227" data-mp="34svjl075yn76">Overview of national security incidents and system activity</p>
      </div>

      {/* Top Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-228" data-mp="1tf1k9uwkfue5">
        <MetricCard title="Total Approved Incidents" value={totalApproved} icon={<ShieldAlert size={20} className="text-blue-600" data-id="element-230" data-mp="4vnkhdl784a6" />} bgColor="bg-blue-50" data-id="element-229" data-mp="1tcyjqgxseiv6" />
        <MetricCard title="Pending Approvals" value={totalPending} icon={<Clock size={20} className="text-amber-600" data-id="element-232" data-mp="dwc0gdu5kepp" />} bgColor="bg-amber-50" onClick={() => onNavigate('Approval')} data-id="element-231" data-mp="8x8tuoyiy0fm" />
        <MetricCard title="Total Casualties" value={totalDeaths + totalInjuries} subtitle={`${totalDeaths} Deaths, ${totalInjuries} Injuries`} icon={<Activity size={20} className="text-purple-600" data-id="element-234" data-mp="skggtb5qge5b" />} bgColor="bg-purple-50" data-id="element-233" data-mp="intyou4sk16z" />
        <MetricCard title="Critical Incidents" value={criticalCount} icon={<AlertTriangle size={20} className="text-red-600" data-id="element-236" data-mp="11kv7yr5dongq" />} bgColor="bg-red-50" data-id="element-235" data-mp="wnwg6f96sslp" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-237" data-mp="2yfr9jerksaf4">
        {/* Charts Section */}
        <div className="lg:col-span-2 space-y-6" data-id="element-238" data-mp="33e2lbv1fkuzz">
          <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm" data-id="element-239" data-mp="2834hr7ztwhl0">
            <div className="flex items-center justify-between mb-4" data-id="element-240" data-mp="284xoenafz1t0">
              <h3 className="text-base font-semibold text-gray-900" data-id="element-241" data-mp="233hzul922d5v">Incident Trend (Last 6 Months)</h3>
              <button onClick={() => onNavigate('Reports')} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1" data-id="element-242" data-mp="2i0ivv0ya4ua5">
                View Full Report <ArrowRight size={14} data-id="element-243" data-mp="1pdocy8h422ys" />
              </button>
            </div>
            <div className="h-64" data-id="element-244" data-mp="1ke20i193wqr1">
              <ResponsiveContainer width="100%" height="100%" data-id="element-245" data-mp="1kev1elldwm19">
                <LineChart data={trendData} margin={{
                top: 5,
                right: 20,
                left: -20,
                bottom: 0
              }} data-id="element-246" data-mp="1z2y5tqjs3pny">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-247" data-mp="1ub6dpbkz5bvp" />
                  <XAxis dataKey="month" tick={{
                  fontSize: 12
                }} tickLine={false} axisLine={false} data-id="element-248" data-mp="1u9vc9if04g7r" />
                  <YAxis tick={{
                  fontSize: 12
                }} tickLine={false} axisLine={false} data-id="element-249" data-mp="16jsmxa2h1ocu" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-250" data-mp="11ky4484i7zsq" />
                  <Line type="monotone" dataKey="count" name="Incidents" stroke="#3b82f6" strokeWidth={3} dot={{
                  r: 4,
                  strokeWidth: 2
                }} activeDot={{
                  r: 6
                }} data-id="element-251" data-mp="3i0uff9f5ucg9" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm" data-id="element-252" data-mp="38bugfq0nrh22">
            <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-253" data-mp="3w26ttgeoa601">Incidents by Severity</h3>
            <div className="h-64 flex items-center justify-center" data-id="element-254" data-mp="4t9ihcve0bms">
              {severityData.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-255" data-mp="3mznwtcxkinlp">
                  <PieChart data-id="element-256" data-mp="3r10zje5d6aoe">
                    <Pie data={severityData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-257" data-mp="3r07q25f0t72k">
                      {severityData.map((entry, index) => {
                    const colorIndex = ['Critical', 'High', 'Medium', 'Low'].indexOf(entry.name);
                    return <Cell key={`cell-${index}`} fill={COLORS[colorIndex !== -1 ? colorIndex : 0]} data-id="element-258" data-mp="cd1zntztxic5" data-mpi={index} />;
                  })}
                    </Pie>
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-259" data-mp="3df6fvh97iu6" />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" data-id="element-260" data-mp="3ujgkmcwimsk1" />
                  </PieChart>
                </ResponsiveContainer> : <p className="text-gray-500 text-sm" data-id="element-261" data-mp="3klk1oyxc6tbd">No data available</p>}
            </div>
          </div>
        </div>

        {/* Recent Activity Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col" data-id="element-262" data-mp="1nsh2vfimt6fp">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between" data-id="element-263" data-mp="1iuysdkoyuj5l">
            <h3 className="text-base font-semibold text-gray-900" data-id="element-264" data-mp="1raobuolhin17">Recent Activity</h3>
            <button onClick={() => onNavigate('Incident')} className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-265" data-mp="1vbgm9l63exz2">
              View All
            </button>
          </div>
          <div className="p-0 flex-1 overflow-y-auto" data-id="element-266" data-mp="251je3i1ha5vt">
            <div className="divide-y divide-gray-100" data-id="element-267" data-mp="254fc16dbsuk9">
              {recentIncidents.length > 0 ? recentIncidents.map(inc => {
              const typeName = initialCaseTypes.find(t => t.id === inc.caseTypeId)?.name || inc.caseTypeId;
              return <div key={inc.id} className="p-4 hover:bg-gray-50 transition-colors" data-id="element-268" data-mp="1hfcxi7dznhtz">
                      <div className="flex justify-between items-start mb-1" data-id="element-269" data-mp="1ma0bhcbd8tub">
                        <span className="text-sm font-medium text-gray-900 truncate pr-2" data-id="element-270" data-mp="2srngjm2xjzl8">{typeName}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${inc.status === 'Approved' ? 'bg-emerald-100 text-emerald-800' : inc.status === 'Submitted' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-271" data-mp="2xpf82eznxjow">
                          {inc.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mb-2" data-id="element-272" data-mp="6d4962m53o8c">
                        {inc.id} • {inc.date}
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-273" data-mp="1gx40c1kigrg">
                        <span className="text-gray-600 truncate max-w-[150px]" data-id="element-274" data-mp="fe27q5dt73jj">{getStateName(inc.stateId)}</span>
                        <span className={`font-medium ${inc.severity === 'Critical' ? 'text-red-600' : inc.severity === 'High' ? 'text-orange-600' : inc.severity === 'Medium' ? 'text-yellow-600' : 'text-green-600'}`} data-id="element-275" data-mp="agaijj4jtvj1">
                          {inc.severity}
                        </span>
                      </div>
                    </div>;
            }) : <div className="p-8 text-center text-sm text-gray-500" data-id="element-276" data-mp="my3bj646etxa">
                  No recent incidents found.
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function MetricCard({
  title,
  value,
  subtitle,
  icon,
  bgColor,
  onClick
}: {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  bgColor: string;
  onClick?: () => void;
}) {
  return <div onClick={onClick} className={`bg-white p-5 rounded-lg border border-gray-200 shadow-sm ${onClick ? 'cursor-pointer hover:border-blue-300 hover:shadow-md transition-all' : ''}`} data-id="element-277" data-mp="3yh2cpmuq1sf">
      <div className="flex items-start justify-between mb-2" data-id="element-278" data-mp="1yc8i86vrxhwb">
        <div className={`p-2 rounded-md ${bgColor}`} data-id="element-279" data-mp="12tyhzrwcrt5g">
          {icon}
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-280" data-mp="17kokmxde9hwg">{title}</h3>
      <div className="flex items-baseline gap-2" data-id="element-281" data-mp="17lzqd2qegzq7">
        <span className="text-2xl font-bold text-gray-900" data-id="element-282" data-mp="swaiyprprzhh">{value}</span>
        {subtitle && <span className="text-xs text-gray-500 font-medium" data-id="element-283" data-mp="xult71lx8c08">{subtitle}</span>}
      </div>
    </div>;
}